
import React, { useRef, useState } from "react";
import { Upload, File, Download } from "lucide-react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { openStorageFile, removeStorageObject } from "@/utils/storageRefs";

interface OrderAttachment {
  id: number;
  file_path: string;
  file_name?: string;
  created_at: string;
}

interface AttachmentsTabProps {
  orderId: number;
  attachments: OrderAttachment[];
  onUpload: (file: File) => void;
  uploading: boolean;
  setAttachments: React.Dispatch<React.SetStateAction<OrderAttachment[]>>;
}

export const AttachmentsTab: React.FC<AttachmentsTabProps> = ({
  orderId,
  attachments,
  onUpload,
  uploading,
  setAttachments,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [removingLoading, setRemovingLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files[0]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (fileRef: string) => {
    // order_files is private: open a signed link, not the stored value.
    const opened = await openStorageFile(fileRef, { defaultBucket: "order_files" });
    if (!opened) {
      toast({
        variant: "destructive",
        title: "File not available",
        description: "Could not get a link to this file.",
      });
    }
  };

  const handleRemove = async (attachment: OrderAttachment) => {
    setRemovingLoading(true);
    try {
      // Remove from database
      const { error } = await supabase
        .from("order_attachments")
        .delete()
        .eq("id", attachment.id);
      if (error) throw error;

      // Remove the object too — canonical ref, public URL or bare path —
      // unless another attachment row still points at it: create-update-order
      // copies attachment rows onto new orders with the same file_path.
      await removeStorageObject(attachment.file_path, "order_files", async () => {
        const { data } = await supabase
          .from("order_attachments")
          .select("id")
          .eq("file_path", attachment.file_path)
          .limit(1);
        return (data ?? []).length > 0;
      });

      toast({
        title: "Attachment removed",
        description: attachment.file_name || attachment.file_path,
      });

      // Remove from local list instead of reloading
      setAttachments(prev => prev.filter(a => a.id !== attachment.id));
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Failed to remove attachment",
        description: err?.message || "Could not remove file.",
      });
    }
    setRemovingLoading(false);
    setRemovingId(null);
  };

  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <input
            type="file"
            accept="*/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
            disabled={uploading}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading..." : "Upload Attachment"}
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {attachments.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <File className="mx-auto h-10 w-10 mb-4" />
            <p>No attachments yet for this order.</p>
          </div>
        ) : (
          <ul>
            {attachments.map((attachment) => (
              <li
                key={attachment.id}
                className="flex items-center justify-between border rounded-md p-3 mb-2"
              >
                <div className="flex items-center gap-3">
                  <File className="h-5 w-5 text-blue-600" />
                  <span className="truncate max-w-[200px]">
                    {attachment.file_name || attachment.file_path}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mr-4 whitespace-nowrap">
                  {new Date(attachment.created_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownload(attachment.file_path)}
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <AlertDialog open={removingId === attachment.id} onOpenChange={open => setRemovingId(open ? attachment.id : null)}>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setRemovingId(attachment.id)}
                        title="Remove"
                        aria-label="Remove"
                        disabled={removingLoading}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Remove Attachment
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove this attachment? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          disabled={removingLoading}
                          onClick={() => setRemovingId(null)}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          disabled={removingLoading}
                          onClick={async () => await handleRemove(attachment)}
                        >
                          {removingLoading ? "Removing..." : "Remove"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
